import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { withTheme } from 'styled-components';
import { Position } from '@blueprintjs/core';
import { select, mouse, event } from 'd3-selection';
import { zoom } from 'd3-zoom';
import { debounce } from 'lodash';

import { Flex } from 'core/components';
import { makeText } from 'core/util/svgUtils';
import { CROSSHATCH_PATH, CROSSHATCH_SIZE } from '../utils/constants';
import { pathgraph } from './utils';
import {
  makeNodeRect,
  makePrefixNodeText,
  makeASNNodeIDText,
  makeASNNodeNameText,
  makeVPASNNodeIcon,
  makeLinkPaths,
  makeLinkHoverPaths,
  makeLinkArrows,
  linkHover
} from './utils/helpers';
import BgpPathGraphPopover from './popover';

@inject('$app', '$dataviews')
@withTheme
@observer
class BgpPathsGraph extends Component {
  state = {
    popoverOpen: false,
    popoverData: null,
    popoverPosition: { left: 0, top: 0, width: 0, height: 0, anchor: Position.BOTTOM },
    xScroll: false
  };

  wrapperRef = React.createRef();

  maxObservedHeight = 0;

  componentDidMount() {
    this.renderData();
    this.renderDataDebounced = debounce(() => this.renderData(), 150);
    window.addEventListener('resize', this.renderDataDebounced);
  }

  componentDidUpdate(prevProps) {
    const { data, hideASName, prefixes, asn } = this.props;

    // Reset max height when the underlying graph data or prefixes change (not on asn hover)
    if (data !== prevProps.data || prefixes !== prevProps.prefixes) {
      this.maxObservedHeight = 0;
    }

    if (
      data !== prevProps.data ||
      hideASName !== prevProps.hideASName ||
      prefixes !== prevProps.prefixes ||
      asn !== prevProps.asn
    ) {
      this.renderData();
      this.closePopover();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.renderDataDebounced);

    const { $dataviews } = this.props;
    $dataviews.unregister(this);
  }

  chartRef = (ref) => {
    const { $dataviews } = this.props;

    if (ref) {
      this.chart = ref;
    }

    $dataviews.register(this);
  };

  isHopSelected = (hop, group) => !!group && !!group[hop.prefix] && !!group[hop.prefix][hop.routeIndex];

  isPathSelected = ({ toHops }, group) => toHops && toHops.find((hop) => this.isHopSelected(hop, group));

  openLinkPopover = (data, i, paths) => {
    clearTimeout(this.popoverTimer);

    const [mouseX, mouseY] = mouse(paths[i]);

    this.popoverTimer = setTimeout(() => {
      const popoverPosition = {
        left: mouseX,
        top: mouseY,
        width: 0,
        height: 0,
        anchor: Position.TOP
      };

      this.setState({
        popoverOpen: true,
        popoverPosition,
        popoverData: data
      });
    }, 100);
  };

  openNodePopover = (data, i, nodes) => {
    clearTimeout(this.popoverTimer);

    this.popoverTimer = setTimeout(() => {
      const wrapperRect = this.wrapperRef.current.getBoundingClientRect();
      const nodeRect = nodes[i].firstChild.getBoundingClientRect();
      const popoverPosition = {
        left: nodeRect.x - wrapperRect.x,
        top: nodeRect.y - wrapperRect.y,
        width: nodeRect.width,
        height: nodeRect.height,
        anchor: Position.TOP
      };

      this.setState({
        popoverOpen: true,
        popoverPosition,
        popoverData: data
      });
    }, 100);
  };

  closePopover = () => {
    const { popoverOpen } = this.state;

    if (popoverOpen) {
      clearTimeout(this.popoverTimer);

      this.popoverTimer = setTimeout(() => {
        this.setState({ popoverOpen: false, popoverData: null });
      }, 100);
    }
  };

  onPopoverHover = () => {
    clearTimeout(this.popoverTimer);
  };

  renderData() {
    const { $app, data, lookups, theme, hideASName, setPrefix, asn, subPrefix: prefix } = this.props;
    const { xScroll } = this.state;
    const { colors } = theme;

    if (!this.chart) {
      return;
    }

    // Preserve the chart container height during redraw to prevent scroll position from resetting.
    // Clearing innerHTML collapses the container; the subsequent style read forces a reflow
    // at 0 height, which causes the browser to adjust scroll position.
    const prevHeight = this.chart.getBoundingClientRect().height;
    this.maxObservedHeight = Math.max(this.maxObservedHeight, prevHeight);
    if (this.maxObservedHeight) {
      this.chart.style.minHeight = `${this.maxObservedHeight}px`;
      this.chart.style.transition = 'min-height 200ms ease';
    }

    this.chart.innerHTML = '';
    const chartContainer = select(this.chart);
    const width = parseInt(chartContainer.style('width')) - 8;
    const tmpSvg = select('body').append('svg').style('position', 'absolute').style('visibility', 'hidden');
    const tmpText = makeText(tmpSvg);
    const options = {
      width,
      lookups,
      tmpText,
      hideASName
    };

    const layout = pathgraph(data, options);
    tmpSvg.remove();
    const vb = layout.bounds.expanded(4);
    const xOverflow = vb.width > width + 8;

    const svg = chartContainer
      .append('svg')
      .attr('viewBox', `${vb.x} ${vb.y} ${width + 8}, ${vb.height}`)
      .attr('height', $app.isExport ? '7in' : vb.height)
      .attr('cursor', xOverflow ? 'grab' : 'default');

    if ($app.isExport) {
      svg.attr('width', '100%');
    }

    // Build cross-hatch pattern definitions for unexpected ASN nodes
    const unexpectedASNs = Object.values(lookups.asns || {}).filter((entry) => entry.expected === false);
    if (unexpectedASNs.length > 0) {
      const defs = svg.append('defs');

      unexpectedASNs.forEach((asnEntry) => {
        const patternId = `hatch-${asnEntry.id}`;
        const pattern = defs
          .append('pattern')
          .attr('id', patternId)
          .attr('width', CROSSHATCH_SIZE)
          .attr('height', CROSSHATCH_SIZE)
          .attr('patternUnits', 'userSpaceOnUse');

        // Solid color background with reduced opacity
        pattern
          .append('rect')
          .attr('width', CROSSHATCH_SIZE)
          .attr('height', CROSSHATCH_SIZE)
          .attr('fill', asnEntry.color)
          .attr('opacity', 0.75);

        // Cross-hatch: both diagonals with reduced opacity
        pattern
          .append('path')
          .attr('d', CROSSHATCH_PATH)
          .attr('stroke', 'rgba(255, 255, 255, 0.6)')
          .attr('stroke-width', 2);
      });
    }

    const g = svg.append('g').attr('cursor', 'default');

    if (xOverflow) {
      const zoomBehavior = zoom()
        .extent([
          [vb.x, vb.y],
          [vb.x + width, vb.y + vb.height]
        ])
        .scaleExtent([1, 1])
        .translateExtent([
          [vb.x, vb.y],
          [vb.x + vb.width, vb.y + vb.height]
        ])
        .on('start', () => svg.attr('cursor', 'grabbing'))
        .on('end', () => svg.attr('cursor', 'grab'))
        .on('zoom', () => g.attr('transform', event.transform));

      svg.call(zoomBehavior);
    }

    const nodeGroup = g.selectAll('.node').data(layout.nodes).enter().append('g').attr('class', 'node');

    const textColor = theme.name === 'light' ? '#252525' : '#fff';
    const lineColor = colors.gray4;
    const lineHoverColor = colors.gray2;

    const nodeRects = makeNodeRect(nodeGroup);
    nodeRects
      .attr('stroke-width', (d) => (prefix === d.id || asn === d.id ? 5 : 2))
      .attr('stroke-dasharray', (d) => (asn === d.id ? '6,3' : 'none'));
    const prefixNodes = nodeGroup.filter((d) => d.type === 'prefix');
    const asnNodes = nodeGroup.filter((d) => d.type === 'asn');

    makePrefixNodeText(prefixNodes, textColor);
    makeASNNodeIDText(asnNodes, textColor, hideASName);
    makeVPASNNodeIcon(
      asnNodes.filter((d) => d.isVp || d.pruned),
      textColor,
      hideASName
    );

    if (!hideASName) {
      makeASNNodeNameText(asnNodes, textColor);
    }

    const linkGroup = g
      .selectAll('.link')
      .data(layout.links)
      .enter()
      .append('g')
      .attr('class', 'link')
      .attr('fill', 'none');

    const linkPaths = makeLinkPaths(linkGroup, lineColor);
    const linkPathHovers = makeLinkHoverPaths(linkGroup);
    const linkArrows = makeLinkArrows(linkGroup, lineColor);

    const handleHoverForLinks = linkHover.bind(null, {
      paths: linkPaths,
      arrows: linkArrows,
      isSelected: this.isPathSelected,
      hoverColor: lineHoverColor,
      color: lineColor
    });

    linkPathHovers
      .on('mouseenter', (d, i, thesePaths) => {
        handleHoverForLinks(d.info.group);
        this.openLinkPopover(d, i, thesePaths);
      })
      .on('mouseleave', () => {
        handleHoverForLinks();
        this.closePopover();
      });

    nodeGroup
      .on('mouseenter', (d, i, nodes) => {
        nodeRects
          .attr('stroke-width', (d2) => (d.id === d2.id || prefix === d2.id || asn === d2.id ? 5 : 2))
          .attr('stroke-dasharray', (d2) => (asn === d2.id ? '6,3' : 'none'));
        handleHoverForLinks(d.group);
        this.openNodePopover(d, i, nodes);
      })
      .on('mouseleave', () => {
        nodeRects
          .attr('stroke-width', (d) => (prefix === d.id || asn === d.id ? 5 : 2))
          .attr('stroke-dasharray', (d) => (asn === d.id ? '6,3' : 'none'));
        handleHoverForLinks();
        this.closePopover();
      });

    prefixNodes.on('click', (d) => setPrefix(d.id));

    // Update max observed height with the new SVG's actual height
    const newHeight = this.chart.getBoundingClientRect().height;
    this.maxObservedHeight = Math.max(this.maxObservedHeight, newHeight);
    this.chart.style.minHeight = `${this.maxObservedHeight}px`;

    if (xOverflow !== xScroll) {
      this.setState({ xScroll: xOverflow });
    }
  }

  render() {
    const { $app, lookups } = this.props;
    const { popoverOpen, popoverData, popoverPosition } = this.state;

    return (
      <Flex
        flexDirection="column"
        display={$app.isExport ? 'block' : 'flex'}
        flex={1}
        position="relative"
        zIndex={1}
        ref={this.wrapperRef}
      >
        <div ref={this.chartRef} />
        <BgpPathGraphPopover
          isOpen={popoverOpen}
          data={popoverData}
          position={popoverPosition}
          lookups={lookups}
          onMouseOver={this.onPopoverHover}
          onMouseOut={this.closePopover}
        />
      </Flex>
    );
  }
}

export default BgpPathsGraph;
